import { z } from "@hono/zod-openapi";
import { routeAcceptedV1 } from "../../routeAssignment/routeAccepted";
import { routeDeclinedV1 } from "../../routeAssignment/routeDeclined";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * A driver's answer to a route assignment.
 *
 * Distinct from the assignment the route reconciler records, which says who is *on* a route; this subject
 * records the driver's answer, accepting or declining, as a moment in the feed. Neither feeds the other.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const routeAssignmentEventKinds = ["routeAccepted", "routeDeclined"] as const satisfies readonly TmsEventKind[];

export type RouteAssignmentEventKind = (typeof routeAssignmentEventKinds)[number];

/**
 * The type of `tms_event.route_assignment_event.facts`: any of this subject's kinds, at any version it declares.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const RouteAssignmentEventFactsSchema = z.discriminatedUnion("kind", [
  routeAcceptedV1.facts,
  routeDeclinedV1.facts,
]);

export type RouteAssignmentEventFacts = z.infer<typeof RouteAssignmentEventFactsSchema>;

/**
 * What a RouteAssignmentEvent normalizer produces: references and facts, without the base the writer supplies.
 */
export type RouteAssignmentEventBody = TmsEventBodyOf<RouteAssignmentEventKind>;
